import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Tracks the most recently auto-filled target (a config field key or a filter
 * condition id) so the UI can briefly highlight it — drawing attention to what
 * the assistant just filled in instead of it changing silently. The token clears
 * itself after `durationMs`, matching the highlight animation, and re-marking a
 * different target replaces it.
 *
 * `nonce` increments on every `mark`, even when the token is unchanged. Consumers
 * depend on it in their highlight effect so that re-marking the *same* token
 * still replays the pulse/scroll (the effect restarts the CSS animation via a
 * reflow) instead of React bailing out on an identical token value.
 *
 * @param durationMs - How long the highlight stays active (defaults to 1500ms)
 * @returns The highlighted token (or null), a monotonically increasing `nonce`,
 *   and a `mark` setter
 */
export function useFillHighlight(durationMs = 1500): {
  token: string | null;
  nonce: number;
  mark: (token: string) => void;
} {
  const [state, setState] = useState<{ token: string | null; nonce: number }>({
    token: null,
    nonce: 0,
  });
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const mark = useCallback(
    (next: string) => {
      if (timerRef.current) clearTimeout(timerRef.current);
      setState(prev => ({ token: next, nonce: prev.nonce + 1 }));
      timerRef.current = setTimeout(
        () => setState(prev => ({ token: null, nonce: prev.nonce })),
        durationMs,
      );
    },
    [durationMs],
  );

  // eslint-disable-next-line no-restricted-syntax -- reason: clear the pending highlight timer on unmount
  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    [],
  );

  return { token: state.token, nonce: state.nonce, mark };
}
